/**
 * status.ts
 * ----------------
 * Selector functions for status view.
 */

import { List } from 'immutable';

import { ActiveLiveSiteEvent } from '../../models/ui/status';
import { ApplicationState } from '../../reducers/status';

/**
 * Selects active live site events from application state.
 * @param state Current application state.
 * @returns Alerts.
 */
export function selectActiveLiveSiteEvents (state: ApplicationState) {
    const { activeLiveSiteEventsResponse } = state.history;

    if (activeLiveSiteEventsResponse === undefined) {
        return List<ActiveLiveSiteEvent>();
    }

    return List(activeLiveSiteEventsResponse.liveSiteEvents).map<ActiveLiveSiteEvent>(liveSiteEvent => ({
        geographies: liveSiteEvent.impacts
            .filter(impact => impact.state === 'Active' || impact.state === 'Mitigated')
            .map(impact => impact.geography)
            .sort(),
        id: liveSiteEvent.id,
        impactStartTimestampUtc: liveSiteEvent.impactStartTimestamp,
        service: liveSiteEvent.impacts[0].service,
        severity: liveSiteEvent.severity,
        title: liveSiteEvent.title,
    })).sortBy(liveSiteEvent => liveSiteEvent.impactStartTimestampUtc).reverse();
}

/**
 * Selects last queried timestamp in UTC format from application state.
 * @param state Current application state.
 * @returns Last queried timestamp.
 */
export function selectQueriedTimestampUtc (state: ApplicationState) {
    return state.status.queriedTimestampUtc;
}

/**
 * Selects recent updates from application state.
 * @param state Current application state.
 * @returns Recent updates.
 */
export function selectRecentUpdates (state: ApplicationState) {
    return state.publicPortal.recentUpdates;
}

/**
 * Selects service metadata from application state.
 * @param state Current application state.
 * @returns Service metadata.
 */
export function selectServices (state: ApplicationState) {
    return state.metadata.services;
}

/**
 * Selects statuses for all services from application state.
 * @param state Current application state.
 * @returns Status documents for all services.
 */
export function selectStatuses (state: ApplicationState) {
    return state.status.statuses;
}
